import {
  getListingPriceContext,
  type ListingPriceContextInput,
} from "@repo/valuation";

export type ListingGlance = {
  sentences: string[];
  text: string;
};

function money(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    notation: value >= 1_000_000 ? "compact" : "standard",
  }).format(value);
}

function rangeLabel(
  tier: "PROFESSIONAL" | "QUICK" | "INDUSTRY",
): string {
  return tier === "PROFESSIONAL"
    ? "Venturu valuation"
    : tier === "QUICK"
      ? "Venturu quick estimate"
      : "industry range";
}

export function buildListingGlance(
  input: ListingPriceContextInput & {
    businessTypeName?: string | null;
  },
): ListingGlance | null {
  const context = getListingPriceContext(input);
  const sentences: string[] = [];

  if (context.yearsOperating != null && context.yearsOperating > 0) {
    sentences.push(`${context.yearsOperating}-year-old business.`);
  }

  if (input.revenue != null || input.sde != null) {
    const revenue = input.revenue != null ? ` on ${money(input.revenue)} revenue` : "";
    const sde = input.sde != null ? `Reported SDE of ${money(input.sde)}${revenue}.` : `Reported revenue of ${money(input.revenue!)}.`;
    sentences.push(sde);
  }

  if (context.totalPrice != null && context.sdeMultiple != null) {
    sentences.push(
      `Asking price is ${context.sdeMultiple.toFixed(1)}x SDE.`,
    );
  } else if (context.totalPrice != null && context.revenueMultiple != null) {
    sentences.push(
      `Asking price is ${context.revenueMultiple.toFixed(1)}x revenue.`,
    );
  }

  const rule = context.ruleOfThumb;
  if (rule?.sdeMultiple && context.sdeMultiple != null) {
    sentences.push(
      `${input.businessTypeName ?? input.industry ?? "Businesses"} typically trade at ${rule.sdeMultiple.low.toFixed(1)}–${rule.sdeMultiple.high.toFixed(1)}x SDE.`,
    );
  } else if (rule?.revenueMultiple && context.revenueMultiple != null) {
    sentences.push(
      `${input.businessTypeName ?? input.industry ?? "Businesses"} typically trade at ${rule.revenueMultiple.low.toFixed(1)}–${rule.revenueMultiple.high.toFixed(1)}x revenue.`,
    );
  }

  const range = context.valuationRange;
  if (context.valuationPosition && range?.min != null && range.max != null) {
    sentences.push(
      `Asking price is ${context.valuationPosition} the ${rangeLabel(range.tier)} of ${money(range.min)}–${money(range.max)}.`,
    );
  }

  if (context.leaseMonthsRemaining != null && context.leaseMonthsRemaining > 0) {
    const leaseYear = new Date(input.leaseExpiration!).getFullYear();
    sentences.push(`Lease runs through ${leaseYear}.`);
  }

  if (sentences.length < 3) return null;
  const selected = sentences.slice(0, 4);
  return { sentences: selected, text: selected.join(" ") };
}
